import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import axios from '../utils/axios';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Redirect if not on first login
  React.useEffect(() => {
    if (!user || !user.isFirstLogin) {
      navigate('/');
    }
  }, [user, navigate]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/change-password', {
        newPassword: formData.newPassword,
        isFirstLogin: true
      });

      if (response.data.success) {
        toast.success('Password changed successfully!');
        // Redirect to dashboard after successful password change
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F8F5] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A5F38] rounded-full shadow-lg mb-4">
            <Lock className="w-8 h-8 text-[#C5A57D]" />
          </div>
          <h1 className="text-3xl font-bold text-[#0A5F38]">First-Time Setup</h1>
        </div>

        <h2 className="mt-6 text-center text-2xl font-bold text-slate-800">
          Change your password
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600">
          Please set a new password for your account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-[0_10px_15px_-3px_rgba(26,82,53,0.05)] border border-slate-100 rounded-xl sm:px-10">
          {/* Alert Box */}
          <div className="mb-6 p-4 bg-[#F8F5E6] border border-[#C5A57D]/20 rounded-lg flex gap-3">
            <AlertCircle className="h-5 w-5 text-[#0A5F38] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#0A5F38]">
                First-time login detected
              </p>
              <p className="text-sm text-slate-600 mt-1">
                You must change your temporary password before accessing the dashboard.
              </p>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* New Password Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-[#C5A57D]" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  className="w-full pl-10 pr-10 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent transition bg-white"
                  placeholder="Enter new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Minimum 6 characters required
              </p>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-[#C5A57D]" />
                </div>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full pl-10 pr-10 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent transition bg-white"
                  placeholder="Confirm password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  disabled={loading}
                >
                  {showConfirm ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A57D] text-[#0A5F38] py-2 px-4 rounded-lg font-bold hover:bg-[#B8985E] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_14px_-2px_rgba(0,0,0,0.1)]"
            >
              {loading ? 'Updating password...' : 'Set Password & Continue'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-slate-500">
            You will be redirected to the dashboard after setting your password.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;

